import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useOrdersListEventListener } from '../../hooks/use-orders-list-event-listener';
import { useStoreEventListener } from '../../hooks/use-store-event-listener';
import { revalidateStoreAccessIfLost } from '../../query/store-access';
import { storeLayout } from '../../route-apis';
import { StoreWebSocketProvider } from '../../websocket/store-websocket-provider';
import { DisabledStore } from '../disabled-store';
import { ProductEditModalPortal } from '../product-table/product-edit-modal-portal';

function StoreLayoutInner({ storeId }: { storeId: string }) {
  useStoreEventListener(storeId);
  useOrdersListEventListener(storeId);

  return (
    <>
      <Outlet />
      <ProductEditModalPortal />
    </>
  );
}

// Component for the /stores/$storeId route.
export function StoreLayoutComponent() {
  const { storeId } = storeLayout.useParams();
  const { store } = storeLayout.useRouteContext();
  const queryClient = useQueryClient();
  const router = useRouter();

  // The socket's only way of saying "I keep being turned away". The store this
  // layout renders comes from a 12-hour-stale, IndexedDB-persisted list, so a
  // tab open when a payment lapses holds enabled:true for the rest of the day
  // and retries $connect every 30s against an authorizer that will never let it
  // in (ORD-479). Re-reading the list is what hands the takeover below its
  // answer; everything else — the unmount, the socket teardown — already works.
  const handleSustainedSocketFailure = useCallback(() => {
    void revalidateStoreAccessIfLost(queryClient, router, storeId);
  }, [queryClient, router, storeId]);

  // Route context is NOT guaranteed populated on every render — the same
  // in-flight-beforeLoad window useCurrentStore guards. When a navigation
  // inside this layout is superseded by a second one, the router republishes
  // this match with status:"success" but an empty context, because lane matches
  // are shallow copies that inherit the previous status while beforeLoad's
  // result is cleared and re-merged around an await. defaultPendingMs: 0 makes
  // that publication eager, so the layout really does render store-less for a
  // frame before beforeLoad refills it.
  //
  // Gate the whole subtree here rather than guarding the takeover below: this
  // layout is the context boundary, and its descendants (useProducts, useStore,
  // every page reading `store` off this route) are typed against beforeLoad's
  // return, so they dereference it unconditionally. Rendering <Outlet/> with an
  // empty context only moves the crash one level down. Render nothing for that
  // frame instead of a spinner — a pending flash is exactly the flicker
  // DelegatingPending (delegating-pending.tsx) exists to avoid.
  if (!store) {
    return null;
  }

  // One takeover for every store route. It also kills the WebSocket $connect,
  // which authorizeStoreAccess would refuse anyway. beforeLoad has already
  // redirected disabled-without-subscription stores to onboarding, so reaching
  // this branch means a lapsed payment — exactly what <DisabledStore/> describes.
  if (!store.enabled) {
    return <DisabledStore />;
  }

  return (
    <StoreWebSocketProvider storeId={storeId} onSustainedFailure={handleSustainedSocketFailure}>
      <StoreLayoutInner storeId={storeId} />
    </StoreWebSocketProvider>
  );
}
